import React from "react";
import { camelizeKeys } from "humps";
import { PAGES } from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { Newsletter, Toast } from "@gonoodle/gn-universe-ui";
import { useRouter } from "next/router";
import { QueryClient, dehydrate } from "@tanstack/react-query";

import api from "../api";
import { useLayoutQuery } from "../hooks";
import Layout from "../components/Layout";
import { QUERY_KEYS, ROUTE_PERMISSIONS } from "../constants";
import { withAuthorization } from "../containers";
import { useLogEvent } from "../contexts/Analytics";
import { appConfigActionsTypes, useAppConfig } from "../contexts/appConfig";
import { useUser } from "../contexts/user";
import { getUtmParamsFromQueryParams } from "../utils/analytics";
import LayoutRenderer from "../components/LayoutRenderer";

const Home = ({ fetchBlueprintStatusCode }) => {
  const router = useRouter();
  const { customHomePageLayoutId: layoutId = "" } = camelizeKeys(router.query);
  const { user, updateUser } = useUser();
  const [appConfigState, appConfigDispatch] = useAppConfig();
  const alert = Toast.useAlert();

  React.useEffect(() => {
    if (appConfigState.toasts.logout) {
      alert.success("You've been successfully logged out");
      appConfigDispatch({
        type: appConfigActionsTypes.SET_LOGOUT_TOAST,
        payload: false,
      });
    }
  }, [alert, appConfigDispatch, appConfigState.toasts.logout]);

  const { layout, isLayoutLoading, layoutError } = useLayoutQuery(layoutId);

  const { logEvent: logNewsletterSignUpEvent } = useLogEvent({
    event: "Newsletter Sign Up",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      enabled: false,
      referrer: {
        sourcePage: PAGES.HOME,
        sourcePageType: PAGES.HOME,
      },
    },
  });

  if (fetchBlueprintStatusCode === 404) {
    return <p className="text-center pt-6">Layout not found.</p>;
  }
  if (fetchBlueprintStatusCode === 403) {
    return <p className="text-center pt-6">Forbidden access.</p>;
  }
  if (fetchBlueprintStatusCode === 204) {
    return <p className="text-center pt-6">Empty layout.</p>;
  }

  const handleNewsletterSignup = (userInfo) => {
    updateUser(userInfo);
    logNewsletterSignUpEvent();
  };

  if (isLayoutLoading) {
    return null;
  }

  if (layoutError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Layout pageTitle="Home" pageDescription="Home Page" progressBar>
      <div className="container pt-sm pb-lg flex flex-col gap-8 md:gap-10 lg:gap-12">
        {Array.isArray(layout) && layout.length > 0 ? (
          <LayoutRenderer layout={layout} />
        ) : (
          <p className="text-white text-center pt-6">Nothing to see here.</p>
        )}
      </div>

      {!user.isAnonymous && !user.isLoggedIn && (
        <Newsletter handleSubmit={handleNewsletterSignup} />
      )}
    </Layout>
  );
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  let fetchBlueprintStatusCode = null;
  const serverClient = api(ctx);
  const queryClient = new QueryClient();
  const { customHomePageLayoutId: layoutId = "" } = camelizeKeys(ctx.query);

  await queryClient.prefetchQuery([QUERY_KEYS.LAYOUT, layoutId], () =>
    serverClient.getLayout(layoutId).then((response) => {
      if (response?.error === "Do not have access, Contact Admin") {
        fetchBlueprintStatusCode = 403;
      }
      if (response?.error === "Invalid custom home page layout ID") {
        fetchBlueprintStatusCode = 404;
      }
      if (response === null) {
        fetchBlueprintStatusCode = 204;
      }
      return response;
    }),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      fetchBlueprintStatusCode,
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

export default Home;
